import { checkGroupNameExists } from "../utils/utils";
import {
    createGroup,
    deleteGroup,
    findGroups,
    updateGroup
    } from "../utils/firestore";
import { Group, GroupOperationResult, GroupWithId } from "../interfaces/IGroups";
import { ParticipantWithId } from "../interfaces/IParticipants";
import {
    useCallback,
    useEffect,
    useMemo,
    useState
    } from "react";
import { useGroupValidation } from "./useGroupValidation";

/**
 * Główny hook do zarządzania grupami
 * Działa jako orkiestrator - koordynuje walidację i operacje
 * Zgodny z zasadą pojedynczej odpowiedzialności - tylko orkiestracja
 */
export const useGroups = (userId: string) => {
  const [groups, setGroups] = useState<GroupWithId[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Hook walidacji
  const { validateGroupData } = useGroupValidation();

  const fetchData = async () => {
    setIsLoading(true);
    if (userId) {
      try {
        const data = await findGroups(userId);
        setGroups(data);
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  // ===== ORKIESTROWANE METODY =====

  /**
   * Dodaje nową grupę - orkiestracja walidacji i operacji
   */
  const handleGroupAdd = async (
    groupData: Omit<Group, "userId">
  ): Promise<GroupOperationResult> => {
    try {
      // 1. Walidacja danych (format nazwy, liczba zawodników)
      const validationErrors = validateGroupData(groupData);
      if (validationErrors.length > 0) {
        return {
          success: false,
          error: validationErrors.join(", "),
        };
      }

      // 2. Sprawdzenie unikalności nazwy (lokalnie z grupami w kontekście)
      if (checkGroupNameExists(groups, groupData.name)) {
        return {
          success: false,
          error: "Grupa o takiej nazwie już istnieje.",
        };
      }

      // 3. Walidacja participantIds (istnienie zawodników, należą do użytkownika)
      // TODO: Można dodać walidację czy wszystkie participantIds istnieją w bazie
      // Na razie zakładamy, że frontend przekazuje poprawne ID

      // 4. Operacja na Firestore
      const result = await createGroup({ ...groupData, userId }, userId);

      // 5. Aktualizacja stanu
      setGroups((prev) => [...prev, result]);

      return { success: true, group: result };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Aktualizuje grupę - orkiestracja walidacji i operacji
   */
  const handleGroupUpdate = async (
    groupId: string,
    updatedData: Partial<Group>
  ): Promise<GroupOperationResult> => {
    try {
      // 1. Znajdź starą grupę
      const oldGroup = groups.find((g) => g.id === groupId);
      if (!oldGroup) {
        return { success: false, error: "Grupa nie została znaleziona" };
      }

      // 2. Przygotuj nowe dane
      const newGroup = { ...oldGroup, ...updatedData };

      // 3. Walidacja danych
      const validationErrors = validateGroupData(newGroup);
      if (validationErrors.length > 0) {
        return {
          success: false,
          error: validationErrors.join(", "),
        };
      }

      // 4. Sprawdzenie unikalności nazwy (z wykluczeniem edytowanej grupy)
      if (updatedData.name && checkGroupNameExists(groups, updatedData.name, groupId)) {
        return {
          success: false,
          error: "Grupa o takiej nazwie już istnieje.",
        };
      }

      // 5. Operacja na Firestore
      await updateGroup(newGroup, userId);

      // 6. Aktualizuj stan lokalny
      setGroups((prev) => prev.map((g) => (g.id === groupId ? newGroup : g)));

      return { success: true, group: newGroup };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Usuwa grupę - orkiestracja operacji
   */
  const handleGroupDelete = async (groupId: string): Promise<GroupOperationResult> => {
    try {
      // 1. Znajdź grupę
      const groupToDelete = groups.find((g) => g.id === groupId);
      if (!groupToDelete) {
        return { success: false, error: "Grupa nie została znaleziona" };
      }

      // 2. Operacja na Firestore
      await deleteGroup(groupId, userId);

      // 3. Aktualizuj stan lokalny
      setGroups((prev) => prev.filter((g) => g.id !== groupId));

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Odświeża listę grup z Firestore
   */
  const refreshGroups = useCallback(async () => {
    await fetchData();
  }, [userId]);

  /**
   * Pobiera grupę po ID
   */
  const getGroupById = useCallback(
    (groupId: string): GroupWithId | undefined => {
      return groups.find((g) => g.id === groupId);
    },
    [groups]
  );

  /**
   * Mapuje ID zawodników z grupy na pełne obiekty Participant
   */
  const getParticipantsByGroupId = useCallback(
    (groupId: string, allParticipants: ParticipantWithId[]): ParticipantWithId[] => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) return [];
      return allParticipants.filter((p) => group.participantIds.includes(p.id || ""));
    },
    [groups]
  );

  return {
    // Stan
    groups,
    isLoading,

    // Orkiestrowane metody
    handleGroupAdd,
    handleGroupUpdate,
    handleGroupDelete,
    refreshGroups,

    // Pomocnicze
    getGroupById,
    getParticipantsByGroupId,
  };
};

